import "./HeroPosters.scss"

import React, { useEffect, useRef, useState } from "react"
import ReactDOM from "react-dom"
import { Icon } from "@fider/components"
import { useFider } from "@fider/hooks"
import { classSet } from "@fider/services"
import { PostCategory } from "@fider/services/khcn"
import { announceBrandLanding } from "@fider/services/brandIntro"
import { HeroTech } from "./HeroScene"
import IconSearch from "@fider/assets/images/heroicons-search.svg"
import IconChevronUp from "@fider/assets/images/chevron-up.svg"
import { i18n } from "@lingui/core"

// Tùy biến KHCN: hero "Sân khấu LED" — poster diễn đàn là màn hình LED lớn giữa hội trường tối, ánh sáng
// màn hình hắt xuống sàn bóng (ảnh phản chiếu), hai diễn giả đứng trước sân khấu trên một đường chân trời vàng,
// mỗi người có bảng tên kính; ô tìm kiếm + chip hạng mục là "bàn điều khiển" ngay trên sàn.
// Poster KHÔNG bao giờ bị cắt (object-fit không dùng); mọi thông tin đọc được đều có bản chữ HTML
// (dải vé sự kiện ≥768px, tiêu đề chữ <768px, bảng tên diễn giả) và bấm poster để xem lớn (lightbox).
// Ảnh: static/hero/event/{market,standee-1,standee-2}-{540,750,1080}.jpg (+ market-1600, market-ambient 480px).
// Bản slider danh lam cũ vẫn ở HeroBanner.tsx (đổi HERO_VARIANT trong services/brand).

interface HeroPostersProps {
  categories: PostCategory[]
  countPerCategory: { [slug: string]: number }
  activeCategory: string | null
  onSelectCategory: (slug: string | null) => void
  onSearch: (query: string) => void
}

type PosterKey = "market" | "standee-1" | "standee-2"

// Thứ tự lặp trong lightbox (← / →)
const ORDER: PosterKey[] = ["market", "standee-1", "standee-2"]

const POSTERS: { [key in PosterKey]: { widths: number[]; ratio: string; alt: string } } = {
  market: {
    widths: [540, 750, 1080, 1600, 2200],
    ratio: "2560 / 1331",
    alt: "Diễn đàn Từ ý tưởng đến thị trường – Kết nối chuyên gia, nhà đầu tư với khởi nghiệp đổi mới sáng tạo Hà Tĩnh năm 2026",
  },
  "standee-1": {
    widths: [360, 540, 750, 1080, 1241],
    ratio: "1241 / 2560",
    alt: "Diễn giả Bùi Đức Anh – Giám đốc đầu tư KBTG: góc nhìn nhà đầu tư về thị trường, mô hình kinh doanh và gọi vốn",
  },
  "standee-2": {
    widths: [360, 540, 750, 1080, 1241],
    ratio: "1241 / 2560",
    alt: "Diễn giả TS. Ngô Đắc Thuần – Chủ tịch HĐQT IP Group: góc nhìn chuyên gia về khởi nghiệp sáng tạo trong công nghệ và nông nghiệp",
  },
}

// Thông tin sự kiện và diễn giả dạng chữ (đọc được ở mọi cỡ màn hình, không phụ thuộc chữ in trong ảnh)
const EVENT = {
  name: "Diễn đàn Đổi mới sáng tạo Hà Tĩnh 2026",
  time: "08:00 · Thứ Sáu 18/09/2026",
  venue: "Khách sạn Đại Bàng, 268 Trần Phú, Hà Tĩnh",
  venueShort: "KS Đại Bàng, 268 Trần Phú",
}

const SPEAKERS: { [key in "standee-1" | "standee-2"]: { name: string; role: string; topic: string } } = {
  "standee-1": {
    name: "Ông Bùi Đức Anh",
    role: "Giám đốc đầu tư KBTG",
    topic: "Góc nhìn nhà đầu tư về thị trường, mô hình kinh doanh và gọi vốn",
  },
  "standee-2": {
    name: "TS. Ngô Đắc Thuần",
    role: "Chủ tịch HĐQT IP Group",
    topic: "Góc nhìn chuyên gia về khởi nghiệp sáng tạo trong lĩnh vực công nghệ và sản phẩm nông nghiệp",
  },
}

// Kích thước hiển thị thực tế của từng poster (để trình duyệt chọn đúng file trong srcset)
const SIZES_SCREEN = "(min-width: 992px) min(66vw, 1180px), (min-width: 768px) calc(100vw - 64px), calc(100vw - 32px)"
const SIZES_STANDEE = "(min-width: 1400px) 280px, (min-width: 992px) calc(14vw + 24px), (min-width: 768px) calc(19vw + 30px), 46vw"

// React 18 chưa có prop fetchPriority; truyền thuộc tính HTML viết thường qua spread
const HIGH_PRIORITY = { fetchpriority: "high" }

export const HeroPosters = (props: HeroPostersProps) => {
  const fider = useFider()
  const base = `${fider.settings.assetsURL}/static/assets/hero/event`
  const [query, setQuery] = useState("")
  const [open, setOpen] = useState<PosterKey | null>(null)
  const openerRef = useRef<HTMLElement | null>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const viewRef = useRef<HTMLDivElement>(null)
  const ticketRef = useRef<HTMLDivElement>(null)

  const src = (key: PosterKey, w: number) => `${base}/${key}-${w}.jpg`
  const srcSet = (key: PosterKey) => POSTERS[key].widths.map((w) => `${src(key, w)} ${w}w`).join(", ")
  const largest = (key: PosterKey) => src(key, POSTERS[key].widths[POSTERS[key].widths.length - 1])

  const openLightbox = (key: PosterKey) => (e: React.MouseEvent<HTMLElement>) => {
    openerRef.current = e.currentTarget
    setOpen(key)
  }
  const step = (delta: number) => setOpen((current) => (current ? ORDER[(ORDER.indexOf(current) + delta + ORDER.length) % ORDER.length] : current))

  // Lightbox: Esc đóng, ←/→ chuyển poster, khoá cuộn nền; focus vào nút đóng khi mở và trả về nút đã bấm khi đóng
  const isOpen = open !== null
  useEffect(() => {
    if (!isOpen) {
      return
    }
    const opener = openerRef.current
    closeRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(null)
      } else if (e.key === "ArrowRight") {
        step(1)
      } else if (e.key === "ArrowLeft") {
        step(-1)
      }
    }
    document.addEventListener("keydown", onKey)
    // Khoá cuộn nền kiểu iOS (overflow: hidden trên body không chặn cuộn cảm ứng của Safari): ghim body tại vị trí
    // đang cuộn, mở xong trả lại đúng vị trí. Đồng thời đặt inert cho #root để Tab/đọc màn hình không lọt ra trang sau.
    const y = window.scrollY
    const b = document.body.style
    const prev = { position: b.position, top: b.top, left: b.left, width: b.width, overflow: b.overflow }
    b.position = "fixed"
    b.top = `-${y}px`
    b.left = "0"
    b.width = "100%"
    b.overflow = "hidden"
    const root = document.getElementById("root")
    root?.setAttribute("inert", "")
    return () => {
      document.removeEventListener("keydown", onKey)
      root?.removeAttribute("inert")
      b.position = prev.position
      b.top = prev.top
      b.left = prev.left
      b.width = prev.width
      b.overflow = prev.overflow
      window.scrollTo(0, y)
      opener?.focus()
    }
  }, [isOpen])

  // Poster ngang ở điện thoại rộng hơn màn hình (cuộn ngang được): mở ra thì cuộn tới giữa, nơi có tiêu đề
  useEffect(() => {
    const view = viewRef.current
    if (view) {
      view.scrollLeft = (view.scrollWidth - view.clientWidth) / 2
    }
  }, [open])

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault()
    props.onSearch(query.trim())
  }

  const standee = (key: "standee-1" | "standee-2", side: "l" | "r") => (
    <>
      <button
        type="button"
        className={`kh-stage__standee kh-stage__standee--${side} no-focus`}
        onClick={openLightbox(key)}
        aria-label={`Xem lớn: ${POSTERS[key].alt}`}
      >
        <img
          src={src(key, 540)}
          srcSet={srcSet(key)}
          sizes={SIZES_STANDEE}
          alt={POSTERS[key].alt}
          width={1241}
          height={2560}
          style={{ aspectRatio: POSTERS[key].ratio }}
          decoding="async"
          loading="eager"
        />
      </button>
      <div className={`kh-stage__plate kh-stage__plate--${side}`}>
        <span className="kh-stage__plate-tag">Diễn giả</span>
        <span className="kh-stage__plate-name">{SPEAKERS[key].name}</span>
        <span className="kh-stage__plate-role">{SPEAKERS[key].role}</span>
        <p className="kh-stage__plate-topic">{SPEAKERS[key].topic}</p>
      </div>
    </>
  )

  const lightboxCaption = open ? (open === "market" ? `${EVENT.time} · ${EVENT.venue}` : SPEAKERS[open].topic) : ""

  // Lần cuộn xuống đầu tiên: bản sao dải vé sự kiện bay vào wordmark trên header, header đổi "2026" → tên diễn đàn
  // (bản gốc vẫn ở lại vì mang thông tin giờ/địa điểm). Dải vé ẩn (<768px) hoặc giảm chuyển động → đổi tên ngay.
  useEffect(() => {
    let fired = false
    const fly = () => {
      const ticket = ticketRef.current
      const target = document.querySelector<HTMLElement>(".c-header__brand-text") || document.querySelector<HTMLElement>(".c-header__brand")
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches
      if (!ticket || !target || reduced || getComputedStyle(ticket).display === "none" || typeof ticket.animate !== "function") {
        announceBrandLanding()
        return
      }
      const from = ticket.getBoundingClientRect()
      const to = target.getBoundingClientRect()
      const ghost = ticket.cloneNode(true) as HTMLElement
      ghost.setAttribute("aria-hidden", "true")
      ghost.style.cssText = `position:fixed;left:${from.left}px;top:${from.top}px;width:${from.width}px;height:${from.height}px;margin:0;z-index:200;pointer-events:none;animation:none;opacity:1;color:#fff;`
      document.body.appendChild(ghost)
      const dx = to.left + to.width / 2 - (from.left + from.width / 2)
      const dy = to.top + to.height / 2 - (from.top + from.height / 2)
      const scale = Math.max(0.12, to.height / from.height)
      const flight = ghost.animate(
        [
          { transform: "translate(0, 0) scale(1)", opacity: 1, offset: 0 },
          { transform: `translate(${dx * 0.4}px, ${dy * 0.3}px) scale(0.7)`, opacity: 1, offset: 0.45 },
          { transform: `translate(${dx}px, ${dy}px) scale(${scale})`, opacity: 0.1, offset: 1 },
        ],
        { duration: 1000, easing: "cubic-bezier(0.75, 0, 0.2, 1)", fill: "forwards" }
      )
      window.setTimeout(announceBrandLanding, 760)
      flight.onfinish = () => ghost.remove()
    }
    const onScroll = () => {
      if (fired || window.scrollY < 40) {
        return
      }
      fired = true
      window.removeEventListener("scroll", onScroll)
      fly()
    }
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  return (
    <section className="kh-stage" aria-label={i18n._({ id: "khcn.hero.region", message: "About the forum" })}>
      {/* Lớp nền tràn viền: ảnh ambient (đã làm mờ sẵn, 480px) + lớp phủ + đèn rọi + sàn tối; không có filter nào chạy mỗi khung hình */}
      <div className="kh-stage__bg" aria-hidden="true">
        <img className="kh-stage__ambient" src={`${base}/market-ambient.jpg`} alt="" decoding="async" />
        <div className="kh-stage__wash" />
        <div className="kh-stage__tech">
          <HeroTech />
        </div>
        <div className="kh-stage__light kh-stage__light--l" />
        <div className="kh-stage__light kh-stage__light--r" />
        <div className="kh-stage__floor" />
      </div>

      <div className="kh-stage__inner container">
        {/* Dải vé sự kiện (≥768px): tên · giờ · địa điểm */}
        <div className="kh-stage__ticket" ref={ticketRef}>
          <span className="kh-stage__ticket-name">{EVENT.name}</span>
          <span className="kh-stage__ticket-sep" aria-hidden="true" />
          <span className="kh-stage__ticket-time">{EVENT.time}</span>
          <span className="kh-stage__ticket-sep kh-stage__ticket-sep--venue" aria-hidden="true" />
          <span className="kh-stage__ticket-venue">{EVENT.venue}</span>
        </div>

        {/* Tiêu đề chữ (<768px): ở điện thoại chữ in trong poster chỉ ~8px nên tiêu đề phải là chữ thật */}
        <div className="kh-stage__headline">
          <p className="kh-stage__eyebrow">Diễn đàn · Hà Tĩnh 2026</p>
          <h2 className="kh-stage__title">
            Từ ý tưởng <em>đến thị trường</em>
          </h2>
          <p className="kh-stage__meta">
            <span>08:00 Thứ Sáu 18/09/2026</span>
            <span className="kh-stage__meta-sep" aria-hidden="true">
              ·
            </span>
            <span>{EVENT.venueShort}</span>
          </p>
        </div>

        <div className="kh-stage__set">
          {/* Đường chân trời vàng nơi màn hình gặp sàn (tràn viền, bị section cắt nên không gây cuộn ngang) */}
          <div className="kh-stage__horizon" aria-hidden="true" />

          {/* Màn hình LED: poster nguyên vẹn trong viền bezel + lớp kính, vệt sáng quét, chớp sáng khi bật máy */}
          <div className="kh-stage__wall">
            <button type="button" className="kh-stage__screen no-focus" onClick={openLightbox("market")} aria-label={`Xem lớn: ${POSTERS.market.alt}`}>
              <img
                src={src("market", 750)}
                srcSet={srcSet("market")}
                sizes={SIZES_SCREEN}
                alt={POSTERS.market.alt}
                width={2560}
                height={1331}
                style={{ aspectRatio: POSTERS.market.ratio }}
                decoding="async"
                loading="eager"
                {...HIGH_PRIORITY}
              />
              <span className="kh-stage__glass" aria-hidden="true" />
              <span className="kh-stage__sweep" aria-hidden="true" />
              <span className="kh-stage__flash" aria-hidden="true" />
              <span className="kh-stage__zoom" aria-hidden="true">
                <Icon sprite={IconSearch} />
                Xem lớn
              </span>
            </button>
            {/* Ảnh phản chiếu của màn hình trên sàn: dùng lại ảnh ambient đã tải (không thêm request, không filter) */}
            <div className="kh-stage__reflect" aria-hidden="true">
              <img src={`${base}/market-ambient.jpg`} alt="" decoding="async" />
            </div>
          </div>

          {standee("standee-1", "l")}
          {standee("standee-2", "r")}

          {/* Bàn điều khiển: ô tìm kiếm + chip hạng mục. <992px: display: contents để form và chip tự xếp vào ô lưới riêng */}
          <div className="kh-stage__console">
            <form className="kh-hero__search kh-stage__search" role="search" onSubmit={submitSearch}>
              <Icon sprite={IconSearch} className="kh-hero__search-icon" />
              <input
                id="kh-hero-search"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={i18n._({ id: "khcn.hero.search.placeholder", message: "Have an idea or proposal?" })}
                aria-label={i18n._({ id: "khcn.hero.search.button", message: "Search" })}
              />
              <button type="submit" className="kh-hero__search-btn no-focus" aria-label={i18n._({ id: "khcn.hero.search.button", message: "Search" })}>
                <Icon sprite={IconSearch} />
                <span>{i18n._({ id: "khcn.hero.search.button", message: "Search" })}</span>
              </button>
            </form>

            <div className="kh-hero__chips kh-stage__chips" role="group">
              <button
                type="button"
                className={classSet({ "kh-hero__chip": true, "no-focus": true, "kh-hero__chip--active": props.activeCategory === null })}
                onClick={() => props.onSelectCategory(null)}
              >
                {i18n._({ id: "khcn.hero.chip.all", message: "All" })}
              </button>
              {props.categories.map((c) => (
                <button
                  type="button"
                  key={c.slug}
                  title={c.hint}
                  className={classSet({ "kh-hero__chip": true, "no-focus": true, "kh-hero__chip--active": props.activeCategory === c.slug })}
                  onClick={() => props.onSelectCategory(c.slug)}
                >
                  {c.label}
                  {props.countPerCategory[c.slug] ? <span className="kh-hero__chip-count">{props.countPerCategory[c.slug]}</span> : null}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox render thẳng vào <body> (portal): không bị overflow/isolation/perspective của banner giam lại.
          Ảnh ngang ở điện thoại được phóng tới 720px và cuộn/pinch được để đọc chữ in trong poster. */}
      {open &&
        typeof document !== "undefined" &&
        ReactDOM.createPortal(
          <div className="kh-stage__lightbox" role="dialog" aria-modal="true" aria-label={POSTERS[open].alt} onClick={() => setOpen(null)}>
            <div className="kh-stage__lightbox-bar" onClick={(e) => e.stopPropagation()}>
              <span className="kh-stage__lightbox-count">
                {ORDER.indexOf(open) + 1} / {ORDER.length}
              </span>
              <button type="button" ref={closeRef} className="kh-stage__lightbox-close no-focus" aria-label="Đóng" onClick={() => setOpen(null)}>
                ✕
              </button>
            </div>
            <button
              type="button"
              className="kh-stage__lightbox-nav kh-stage__lightbox-nav--prev no-focus"
              aria-label="Poster trước"
              onClick={(e) => {
                e.stopPropagation()
                step(-1)
              }}
            >
              <Icon sprite={IconChevronUp} />
            </button>
            <div ref={viewRef} className={classSet({ "kh-stage__lightbox-view": true, "kh-stage__lightbox-view--wide": open === "market" })}>
              <img
                key={open}
                src={largest(open)}
                alt={POSTERS[open].alt}
                width={open === "market" ? 2560 : 1241}
                height={open === "market" ? 1331 : 2560}
                style={{ aspectRatio: POSTERS[open].ratio }}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <button
              type="button"
              className="kh-stage__lightbox-nav kh-stage__lightbox-nav--next no-focus"
              aria-label="Poster sau"
              onClick={(e) => {
                e.stopPropagation()
                step(1)
              }}
            >
              <Icon sprite={IconChevronUp} />
            </button>
            <div className="kh-stage__lightbox-caption" onClick={(e) => e.stopPropagation()}>
              <strong>{open === "market" ? EVENT.name : SPEAKERS[open].name + " – " + SPEAKERS[open].role}</strong>
              <span>{lightboxCaption}</span>
            </div>
          </div>,
          document.body
        )}
    </section>
  )
}
